import { required, type Court, type Match, type Tournament } from "./model";
import { assignEliminationReferees } from "./referees";

export function venueParts(
  at: string,
  timezone: string,
): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(at));
  const get = (name: string) =>
    required(parts.find((p) => p.type === name)).value;
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}
export function venueTime(
  date: string,
  time: string,
  timezone: string,
): string {
  const target = Date.parse(`${date}T${time}:00Z`);
  let guess = target;
  for (let i = 0; i < 3; i++) {
    const parts = venueParts(new Date(guess).toISOString(), timezone);
    guess += target - Date.parse(`${parts.date}T${parts.time}:00Z`);
  }
  return new Date(guess).toISOString();
}
export function courtOpen(
  court: Court,
  now: string,
  timezone: string,
): boolean {
  const { date, time } = venueParts(now, timezone);
  return court.windows.some(
    (w) => w.date === date && w.opens <= time && w.closes > time,
  );
}
export function activeTeamIds(t: Tournament, excluding?: string): Set<string> {
  const ids = new Set<string>();
  for (const match of t.matches) {
    if (match.status !== "live" || match.id === excluding) continue;
    match.teams.forEach((id) => {
      if (id) ids.add(id);
    });
    if (match.refereeId) ids.add(match.refereeId);
  }
  for (const heat of t.heats)
    if (heat.status === "live" && heat.id !== excluding)
      heat.teamIds.forEach((id) => ids.add(id));
  return ids;
}
export function refereeCandidates(t: Tournament, match: Match): string[] {
  const pool = match.poolId && t.pools.find((p) => p.id === match.poolId);
  const busy = activeTeamIds(t, match.id);
  const completed = t.matches
    .filter((m) => m.status === "complete" && m.resolution !== "bye")
    .toSorted((a, b) =>
      (a.completedAt ?? "").localeCompare(b.completedAt ?? ""),
    );
  const previous = (
    pool ? completed.filter((m) => m.poolId === pool.id) : completed
  ).at(-1);
  const eligible = t.teams.filter((team) => {
    if (
      team.withdrawn ||
      match.teams.includes(team.id) ||
      busy.has(team.id) ||
      (pool && !pool.teamIds.includes(team.id))
    )
      return false;
    const lastLoss = completed.findLast((m) => m.loserId === team.id);
    const hasFuture = t.matches.some(
      (m) => m.status !== "complete" && m.teams.includes(team.id),
    );
    const dutyAfterLoss =
      lastLoss &&
      completed.some(
        (m) =>
          m.refereeId === team.id &&
          (m.completedAt ?? "") > (lastLoss.completedAt ?? ""),
      );
    return hasFuture || !lastLoss || !dutyAfterLoss;
  });
  return eligible
    .sort((a, b) => {
      const cost = (id: string) =>
        (previous?.loserId === id ? -10 : 0) +
        (previous?.teams.includes(id) ? -6 : 0) +
        completed.filter((m) => m.refereeId === id).length * 2 +
        (previous?.refereeId === id ? 4 : 0);
      return cost(a.id) - cost(b.id) || a.seed - b.seed;
    })
    .map((team) => team.id);
}
export function assignPoolReferees(t: Tournament): boolean {
  let changed = false;
  for (const pool of t.pools) {
    const matches = t.matches.filter((m) => m.poolId === pool.id);
    const eligible = pool.teamIds.filter(
      (id) => !t.teams.find((team) => team.id === id)?.withdrawn,
    );
    const duties = new Map<string, number>();
    for (const match of matches) {
      if (
        match.refereeMode === "auto" &&
        match.status !== "live" &&
        match.status !== "complete" &&
        match.refereeId &&
        (!eligible.includes(match.refereeId) ||
          match.teams.includes(match.refereeId))
      ) {
        delete match.refereeId;
        match.revision++;
        changed = true;
      }
      if (match.refereeId)
        duties.set(match.refereeId, (duties.get(match.refereeId) ?? 0) + 1);
    }
    for (const [index, match] of matches.entries()) {
      if (
        match.refereeMode !== "auto" ||
        match.refereeId ||
        match.status === "complete" ||
        match.status === "live"
      )
        continue;
      const previous = matches[index - 1];
      const next = matches[index + 1];
      const cost = (id: string) =>
        (duties.get(id) ?? 0) * 10 +
        (previous?.refereeId === id ? 4 : 0) -
        (previous?.teams.includes(id) ? 3 : 0) +
        (next?.teams.includes(id) ? 2 : 0);
      const referee = eligible
        .filter((id) => !match.teams.includes(id))
        .sort((a, b) => cost(a) - cost(b))[0];
      if (!referee) continue;
      match.refereeId = referee;
      match.revision++;
      duties.set(referee, (duties.get(referee) ?? 0) + 1);
      changed = true;
    }
  }
  return changed;
}
export function dispatch(t: Tournament, now: string): boolean {
  let changed = assignPoolReferees(t);
  if (assignEliminationReferees(t)) changed = true;
  if (t.status !== "running") return changed;
  const occupied = new Set([
    ...t.matches.filter((m) => m.status === "live").map((m) => m.courtId),
    ...t.heats.filter((h) => h.status === "live").map((h) => h.courtId),
  ]);
  const recent = t.matches
    .filter((m) => m.completedAt)
    .toSorted((a, b) => b.completedAt!.localeCompare(a.completedAt!));
  const restCost = (m: Match) =>
    m.teams.reduce(
      (cost, id) =>
        cost +
        (recent[0]?.teams.includes(id) ? 4 : 0) +
        (recent[0]?.refereeId === id ? 2 : 0),
      0,
    );
  const ready = t.matches
    .filter((m) => m.status === "ready")
    .sort(
      (a, b) =>
        a.wave - b.wave ||
        (a.poolId && b.poolId ? 0 : restCost(a) - restCost(b)),
    );
  for (const match of ready) {
    if (match.needsRelease && !match.released) continue;
    if (match.notBefore && Date.parse(match.notBefore) > Date.parse(now))
      continue;
    const busy = activeTeamIds(t);
    if (match.teams.some((id) => id && busy.has(id))) continue;
    const pool = match.poolId && t.pools.find((p) => p.id === match.poolId);
    const courts = t.courts.filter(
      (c) =>
        !occupied.has(c.id) &&
        courtOpen(c, now, t.timezone) &&
        (!pool || c.id === pool.courtId) &&
        (!match.courtId || match.courtId === c.id),
    );
    courts.sort(
      (a, b) =>
        Number(!!b.netTeamId && match.teams.includes(b.netTeamId)) -
        Number(!!a.netTeamId && match.teams.includes(a.netTeamId)),
    );
    const court = courts[0];
    if (!court) continue;
    if (match.refereeMode === "auto") {
      const referee =
        pool || match.refereeSlot
          ? match.refereeId
          : refereeCandidates(t, match)[0];
      if (!referee || busy.has(referee)) continue;
      match.refereeId = referee;
    } else if (
      match.refereeMode === "team" &&
      (!match.refereeId ||
        busy.has(match.refereeId) ||
        match.teams.includes(match.refereeId))
    )
      continue;
    match.courtId = court.id;
    match.startedAt = now;
    match.scoring = structuredClone(
      t.config.stageScoring[match.stage] ?? t.config.scoring,
    );
    match.status = "live";
    match.revision++;
    occupied.add(court.id);
    changed = true;
  }
  return changed;
}

export function schedule(
  t: Tournament,
  now: string,
): Array<{ id: string; courtId?: string; estimatedAt?: string }> {
  const ends = new Map<string, number>();
  const available = new Map<string, number>();
  const teamAvailable = new Map<string, number>();
  const duration = t.config.estimatedMatchMinutes * 60000;
  for (const match of t.matches.filter((m) => m.status === "live")) {
    const end = Math.max(
      Date.parse(now),
      Date.parse(match.startedAt!) + duration,
    );
    ends.set(match.id, end);
    available.set(match.courtId!, end);
    for (const id of [...match.teams, match.refereeId])
      if (id) teamAvailable.set(id, end);
  }
  const nextWindow = (court: Court, after: number) => {
    for (const w of court.windows.toSorted((a, b) =>
      a.date.localeCompare(b.date),
    )) {
      const open = Date.parse(venueTime(w.date, w.opens, t.timezone));
      const close = Date.parse(venueTime(w.date, w.closes, t.timezone));
      const start = Math.max(after, open);
      if (start + duration <= close) return start;
    }
    return Infinity;
  };
  return t.matches.map((match) => {
    if (match.status === "live")
      return {
        id: match.id,
        courtId: match.courtId,
        estimatedAt: match.startedAt,
      };
    if (match.completedAt) {
      ends.set(match.id, Date.parse(match.completedAt));
      return {
        id: match.id,
        courtId: match.courtId,
        estimatedAt: match.startedAt,
      };
    }
    if (match.resolution === "bye") {
      ends.set(match.id, 0);
      return { id: match.id };
    }
    const dependencies = match.slots.flatMap((s) =>
      "matchId" in s ? [ends.get(s.matchId) ?? Date.parse(now)] : [],
    );
    if (match.refereeSlot && "matchId" in match.refereeSlot)
      dependencies.push(ends.get(match.refereeSlot.matchId) ?? Date.parse(now));
    const earliest = Math.max(
      Date.parse(now),
      ...dependencies,
      ...[...match.teams, match.refereeId].map((id) =>
        id ? (teamAvailable.get(id) ?? 0) : 0,
      ),
      Date.parse(match.notBefore ?? now),
    );
    const choices = t.courts
      .filter((c) => !match.courtId || c.id === match.courtId)
      .map((c) => ({
        court: c,
        start: nextWindow(c, Math.max(earliest, available.get(c.id) ?? 0)),
      }));
    choices.sort((a, b) => a.start - b.start);
    const choice = choices[0];
    if (!choice || !Number.isFinite(choice.start))
      return { id: match.id, courtId: match.courtId };
    const start = match.startedAt ? Date.parse(match.startedAt) : choice.start;
    const end = Math.max(Date.parse(now), start + duration);
    ends.set(match.id, end);
    available.set(choice.court.id, end);
    for (const id of [...match.teams, match.refereeId])
      if (id) teamAvailable.set(id, end);
    return {
      id: match.id,
      courtId: choice.court.id,
      estimatedAt: new Date(start).toISOString(),
    };
  });
}
